import { useCallback, useContext, useEffect, useState } from 'react';
import { ContextoAuth } from './contextoAuth';
import { contarTodo, enviarTodo } from '../integrador/pwa/pendientes';

// Estado de conexion al nodo + cuanto trabajo hay guardado en el telefono.
// Al volver la conexion, reenvia sola las colas. Un sondeo ligero mantiene
// el contador al dia cuando otras pantallas encolan.
//
// DIRECTRIZ-PWA F2 (11 oct 2026, DISENO-F2 §5): la cuenta y el envío viven
// en `integrador/pwa/pendientes.js`, que lee las SIETE fuentes (movimientos,
// conteos, recepciones, lecturas, traslados, ventas, capturas) y las manda
// en el orden que no deja a una venta sin la existencia que una entrada
// acaba de crear. Antes la franja sumaba tres y las llamaba «movimientos».
// La forma de lo que devuelve este hook no cambia: `online`, `enCola`,
// `rechazados`, `refrescar`, `flush`, `descartarRechazados`; gana
// `porRevisar` y `otraCuenta`.
export function useConexion() {
  const usuario = useContext(ContextoAuth)?.usuario || null;
  const [online, setOnline] = useState(navigator.onLine);
  const [cuenta, setCuenta] = useState({ pendientes: 0, porRevisar: 0, otraCuenta: 0 });
  const [rechazados, setRechazados] = useState([]);

  const refrescar = useCallback(async () => {
    const c = await contarTodo(usuario).catch(() => null);
    if (c) setCuenta(c);
  }, [usuario]);

  const flush = useCallback(async () => {
    if (!navigator.onLine) return;
    // Las recepciones rechazadas llegan marcadas: una recepción rechazada
    // es mercancía que ya está en el andén y que nadie va a registrar si
    // el aviso no lo dice. El espejo persistente lo guarda `enviarTodo`.
    const r = await enviarTodo(usuario).catch(() => ({ rechazados: [] }));
    if (r.rechazados.length) setRechazados((prev) => [...prev, ...r.rechazados]);
    await refrescar();
  }, [usuario, refrescar]);

  useEffect(() => {
    // La primera lectura va por la promesa y no llamando a `refrescar()`
    // en el cuerpo del efecto: aunque el setState de dentro ocurra tras un
    // await, la regla no puede saberlo y avisa. Así queda explícito y de
    // paso se ignora el resultado si la pantalla se desmontó antes.
    let vivo = true;
    contarTodo(usuario).then((c) => { if (vivo && c) setCuenta(c); }).catch(() => {});

    const alConectar = () => { setOnline(true); flush(); };
    const alDesconectar = () => setOnline(false);
    window.addEventListener('online', alConectar);
    window.addEventListener('offline', alDesconectar);
    const iv = setInterval(() => { refrescar(); if (navigator.onLine) flush(); }, 5000);
    return () => {
      vivo = false;
      window.removeEventListener('online', alConectar);
      window.removeEventListener('offline', alDesconectar);
      clearInterval(iv);
    };
  }, [usuario, refrescar, flush]);

  const descartarRechazados = useCallback(() => setRechazados([]), []);
  return {
    online, enCola: cuenta.pendientes, porRevisar: cuenta.porRevisar, otraCuenta: cuenta.otraCuenta,
    rechazados, refrescar, flush, descartarRechazados,
  };
}
